/**
 * Standard product GraphQL query
 */
export const productQuery = `
  id
  title
  handle
  tags
  productType
  publishedAt
  options {
    name,
    values
  }
  images(first: 2) {
    edges {
      node {
        altText
        url
      }
    }
  }
  priceRange {
    maxVariantPrice {
      amount
    }
    minVariantPrice {
      amount
    }
  }
  collections(first: 50) {
    edges {
      node {
        id
        handle
        metafield(namespace: "custom", key: "messaging_strip_text") {
          value
        }
      }
    }
  }
  metafield(namespace: "custom", key: "ranking_priority") {
    value
  }
  variants(first: 10) {
    edges {
      node {
        id
        availableForSale
        selectedOptions {
          value
        }
        compareAtPrice {
          amount
        }
        storeAvailability(first: 10) {
          nodes {
              available
              location {
                  id
                  name
                  address {
                    zip
                    provinceCode
                  }
              }
              quantityAvailable
          }
        }
      }
    }
  }
`

export const collectionNumberOfProductsQuery = `
  edges {
    node {
      id
      handle
      title
      products (first: 50) {
        edges {
          node {
            id
          }
        }
      }
    }
  }
`

/**
 * The products loaded from graphQL need to be edited to
 * - name variables to match liquid versions
 * - set URL correctly
 * - fix IDs with atob()
 * - set pricing correctly
 */
export function transformGraphqlProduct (productNode, collectionHandle = null) {
  if (!productNode) return null

  // Extract messaging strip text from collections (first match)
  let messagingStripText = null
  if (productNode.collections?.edges) {
    for (const { node: collection } of productNode.collections.edges) {
      if (collection.metafield?.value) {
        messagingStripText = collection.metafield.value
        break
      }
    }
  }
  // Extract ranking_priority metafield
  // Note: Metafield namespace should be 'custom' with key 'ranking_priority'
  // If using a different namespace, update the GraphQL query accordingly
  let rankingPriority = null
  if (productNode.metafield?.value) {
    const parsed = parseInt(productNode.metafield.value, 10)
    // Validate that parseInt returned a valid number (not NaN) and is within expected range (1-10)
    if (!isNaN(parsed) && parsed >= 1 && parsed <= 10) {
      rankingPriority = parsed
    }
  }

  return {
    objectType: 'product',
    handle: productNode.handle,
    url: collectionHandle ? `/collections/${collectionHandle}/products/${productNode.handle}` : `/products/${productNode.handle}`,
    id: parseInt(productNode.id.replace('gid://shopify/Product/', '')),
    tags: productNode.tags.map(tag => tag.toLowerCase()),
    productType: productNode.productType,
    title: productNode.title,
    publishedAt: productNode.publishedAt,
    ranking_priority: rankingPriority,
    variants: productNode.variants.edges.map(({ node }) => {
      return {
        id: parseInt(node.id.replace('gid://shopify/ProductVariant/', '')),
        available: node.availableForSale,
        compare_at_price: node.compareAtPrice?.amount * 100 || null,
        options: node.selectedOptions.map(option => option.value)
      }
    }),
    price: productNode.priceRange.minVariantPrice.amount * 100,
    compare_at_price: productNode.variants.edges[0].node.compareAtPrice?.amount * 100 || null,
    media: productNode.images.edges.map(image => {
      return {
        alt: image.node.altText,
        media_type: 'image',
        src: image.node.url
      }
    }),
    has_only_default_variant: productNode.options[0].values[0] === 'Default Title',
    options: productNode.options.map(option => option.name),
    options_with_values: productNode.options.map(option => {
      return {
        name: option.name,
        position: productNode.options.indexOf(option) + 1,
        values: option.values
      }
    }),
    available_on: productNode.variants?.edges[0].node.storeAvailability?.nodes.filter(node => node.available).map(item => item.location.address?.provinceCode),
    messaging_strip_text: messagingStripText
  }
}

/**
 * The products loaded from Shopify Product Ajax API need to be edited to
 * - conform to Liquid Project object
 */
export function transformAjaxProduct (product) {
  product.has_only_default_variant = product.options[0].values[0] === 'Default Title'
  product.options_with_values = product.options
  product.options = product.options.map(option => option.name)
  return product
}

/**
 * Liquid Product object JSON does not have options_with_values property,
 * so we need to build this ourselves.
 * @param {Object} product
 * @returns {Array} array of objects - options with values
 */
export function getProductOptions (product) {
  if (product.options_with_values) {
    return product.options_with_values
  } else {
    const optionsWithValues = []

    if (product.variants[0].title !== 'Default Title') {
      for (const [index, option] of Object.entries(product.options)) {
        const position = parseInt(index) + 1
        const values = new Set() // Use Set to collect unqiue values

        for (const variant of product.variants) {
          values.add(variant[`option${position}`])
        }

        const optionWithValues = {
          name: option,
          position,
          values: [...values]
        }

        optionsWithValues.push(optionWithValues)
      }
    }

    return optionsWithValues
  }
}

/**
 * @param {Object} data - GraphQL product response
 * @param {number} [variantId] - Optional variant ID to get data for (for multi-variant products)
 */
export function transformStoreLocation (data, variantId = null) {
  const edges = data.product?.variants?.edges || []
  const variantNode = variantId
    ? edges.find(e => parseInt(e.node.id.replace('gid://shopify/ProductVariant/', '')) === variantId)?.node
    : edges[0]?.node

  if (!variantNode) return null

  const storeNodes = variantNode.storeAvailability?.nodes || []
  const availableStores = storeNodes.filter(n => n.available)
  const availableOn = availableStores.map(n => n.location?.address?.provinceCode).filter(Boolean)

  return {
    variants: {
      id: parseInt(variantNode.id.replace('gid://shopify/ProductVariant/', '')),
      store: storeNodes,
      available_on: [...new Set(availableOn)],
      availableStores
    }
  }
}

/**
 * Pick closest warehouse by state and postcode proximity
 * @param {Array} availableStores - From transformStoreLocation variants.availableStores
 * @param {string} state - e.g. 'NSW'
 * @param {string} postcode - e.g. '2000'
 * @returns {{ name: string, address: object } | null}
 */
export function getClosestWarehouse (availableStores, state, postcode) {
  if (!availableStores?.length || !state) return null

  const inState = availableStores.filter(s =>
    s.location?.address?.provinceCode === state
  )
  if (!inState.length) return null

  const postcodeNum = parseInt(postcode, 10)
  if (isNaN(postcodeNum)) {
    return { name: inState[0].location?.name, address: inState[0].location?.address }
  }

  inState.sort((a, b) => {
    const zipA = parseInt(a.location?.address?.zip || '9999', 10)
    const zipB = parseInt(b.location?.address?.zip || '9999', 10)
    return Math.abs(zipA - postcodeNum) - Math.abs(zipB - postcodeNum)
  })

  return { name: inState[0].location?.name, address: inState[0].location?.address }
}

export function collectionProductsRequest (handle, productArguments) {
  return `{
    collection(handle: "${handle}") {
      id
      products(${productArguments}) {
        filters {
          id
          label
          type
          values {
            id
            label
            count
            input
          }
        }
        edges {
          node {
            ${productQuery}
          }
          cursor
        }
        pageInfo {
          hasNextPage
        }
      }
    }
  }
`
}

export function searchProductsRequest (query, productArguments) {
  return `{
    search(query: "${query}", ${productArguments}) {
      edges {
        node {
          ... on Product {
            ${productQuery}
          }
        }
        cursor
      }
      productFilters {
        label
        type
        id
        values {
          id
          label
          count
          input
        }
      }
      pageInfo {
        hasNextPage
      }
    }
  }
`
}

export function variantStockInLocationRequest (productId) {
  return `{
    product(id: "gid://shopify/Product/${productId}") {
      title
      variants(first: 100) {
        edges {
          node {
            id
            availableForSale
            storeAvailability(first: 10) {
              nodes {
                  available
                  location {
                      id
                      name
                      address {
                        zip
                        provinceCode
                      }
                  }
                  quantityAvailable
              }
            }
          }
        }
      }
      }
    }`
}
